import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { format, formatDistanceToNow } from "date-fns";
import {
  AlertCircle,
  Calendar,
  ChevronRight,
  Clock,
  MessageSquare,
  Search,
  Send,
  User,
} from "lucide-react";
import { useState } from "react";

type TicketStatus = "open" | "in_progress" | "resolved" | "closed";
type TicketPriority = "low" | "medium" | "high" | "urgent";
type TicketCategory =
  | "bug_report"
  | "feature_request"
  | "account_issue"
  | "billing"
  | "content_feedback"
  | "other";

interface SupportTicket {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  subject: string;
  description: string;
  category: TicketCategory;
  priority: TicketPriority;
  status: TicketStatus;
  createdAt: string;
  updatedAt: string;
  assignedTo?: string;
  messages: TicketMessage[];
}

interface TicketMessage {
  id: string;
  ticketId: string;
  senderType: "user" | "admin";
  senderName: string;
  message: string;
  createdAt: string;
}

const statusLabels: Record<TicketStatus, string> = {
  open: "Open",
  in_progress: "In Progress",
  resolved: "Resolved",
  closed: "Closed",
};

const statusColors: Record<TicketStatus, string> = {
  open: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  in_progress:
    "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  resolved:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  closed: "bg-muted text-muted-foreground",
};

const priorityLabels: Record<TicketPriority, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  urgent: "Urgent",
};

const priorityColors: Record<TicketPriority, string> = {
  low: "bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-400",
  medium: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  high: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  urgent: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
};

const categoryLabels: Record<TicketCategory, string> = {
  bug_report: "Bug Report",
  feature_request: "Feature Request",
  account_issue: "Account Issue",
  billing: "Billing",
  content_feedback: "Content Feedback",
  other: "Other",
};

const mockTickets: SupportTicket[] = [
  {
    id: "1",
    userId: "user-1",
    userEmail: "sarah@example.com",
    userName: "Sarah Johnson",
    subject: "Unable to access meditation content",
    description:
      "I've been trying to access the morning meditation series but keep getting an error message. This has been happening for the past two days.",
    category: "bug_report",
    priority: "high",
    status: "open",
    createdAt: "2026-01-07T08:30:00Z",
    updatedAt: "2026-01-07T08:30:00Z",
    messages: [
      {
        id: "m1",
        ticketId: "1",
        senderType: "user",
        senderName: "Sarah Johnson",
        message:
          "I've been trying to access the morning meditation series but keep getting an error message. This has been happening for the past two days.",
        createdAt: "2026-01-07T08:30:00Z",
      },
    ],
  },
  {
    id: "2",
    userId: "user-2",
    userEmail: "emma@example.com",
    userName: "Emma Wilson",
    subject: "Subscription billing question",
    description:
      "I was charged twice this month for my subscription. Can you please help me resolve this?",
    category: "billing",
    priority: "urgent",
    status: "in_progress",
    createdAt: "2026-01-06T14:20:00Z",
    updatedAt: "2026-01-07T09:15:00Z",
    assignedTo: "Admin",
    messages: [
      {
        id: "m2",
        ticketId: "2",
        senderType: "user",
        senderName: "Emma Wilson",
        message:
          "I was charged twice this month for my subscription. Can you please help me resolve this?",
        createdAt: "2026-01-06T14:20:00Z",
      },
      {
        id: "m3",
        ticketId: "2",
        senderType: "admin",
        senderName: "Support Team",
        message:
          "Hi Emma, thank you for reaching out. I'm looking into this now and will get back to you shortly with a resolution.",
        createdAt: "2026-01-07T09:15:00Z",
      },
    ],
  },
  {
    id: "3",
    userId: "user-3",
    userEmail: "lisa@example.com",
    userName: "Lisa Chen",
    subject: "Request for more breathing exercises",
    description:
      "I love the app! Would it be possible to add more breathing exercise content? The current ones have been really helpful.",
    category: "feature_request",
    priority: "low",
    status: "resolved",
    createdAt: "2026-01-05T11:45:00Z",
    updatedAt: "2026-01-06T16:30:00Z",
    assignedTo: "Content Team",
    messages: [
      {
        id: "m4",
        ticketId: "3",
        senderType: "user",
        senderName: "Lisa Chen",
        message:
          "I love the app! Would it be possible to add more breathing exercise content? The current ones have been really helpful.",
        createdAt: "2026-01-05T11:45:00Z",
      },
      {
        id: "m5",
        ticketId: "3",
        senderType: "admin",
        senderName: "Content Team",
        message:
          "Thank you for your wonderful feedback, Lisa! We're thrilled to hear the breathing exercises have been helpful. We have new content coming next month that includes 10 new breathing exercise sessions!",
        createdAt: "2026-01-06T16:30:00Z",
      },
    ],
  },
  {
    id: "4",
    userId: "user-4",
    userEmail: "maria@example.com",
    userName: "Maria Santos",
    subject: "Cannot update profile picture",
    description:
      "When I try to upload a new profile picture, the app crashes. I'm using iPhone 14 with iOS 17.",
    category: "bug_report",
    priority: "medium",
    status: "open",
    createdAt: "2026-01-07T06:00:00Z",
    updatedAt: "2026-01-07T06:00:00Z",
    messages: [
      {
        id: "m6",
        ticketId: "4",
        senderType: "user",
        senderName: "Maria Santos",
        message:
          "When I try to upload a new profile picture, the app crashes. I'm using iPhone 14 with iOS 17.",
        createdAt: "2026-01-07T06:00:00Z",
      },
    ],
  },
];

export default function UserSupport() {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(
    null,
  );
  const [replyMessage, setReplyMessage] = useState("");
  const { toast } = useToast();
  const { user } = useAuth();

  const tickets = mockTickets;
  const isLoading = false;

  const filteredTickets = tickets.filter((ticket) => {
    const matchesSearch =
      ticket.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.userEmail.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus =
      statusFilter === "all" || ticket.status === statusFilter;
    const matchesPriority =
      priorityFilter === "all" || ticket.priority === priorityFilter;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const openTickets = tickets.filter((t) => t.status === "open").length;
  const inProgressTickets = tickets.filter(
    (t) => t.status === "in_progress",
  ).length;
  const urgentTickets = tickets.filter(
    (t) =>
      t.priority === "urgent" &&
      t.status !== "resolved" &&
      t.status !== "closed",
  ).length;

  const handleSendReply = () => {
    if (!replyMessage.trim() || !selectedTicket) return;

    toast({
      title: "Reply sent",
      description: "Your response has been sent to the user.",
    });
    setReplyMessage("");
  };

  const handleUpdateStatus = (newStatus: TicketStatus) => {
    if (!selectedTicket) return;

    toast({
      title: "Status updated",
      description: `Ticket status changed to ${statusLabels[newStatus]}.`,
    });
  };

  return (
    <div className="min-h-screen p-6 lg:p-8 space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1
            className="text-2xl font-semibold text-foreground mb-1"
            data-testid="text-page-title"
          >
            User Support
          </h1>
          <p
            className="text-sm text-muted-foreground"
            data-testid="text-page-subtitle"
          >
            Manage support requests and complaints from mobile app users
          </p>
        </div>
      </div>

      <div className="grid gap-4 grid-cols-3">
        <Card data-testid="card-stat-open">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-foreground">
                  {openTickets}
                </p>
                <p className="text-xs text-muted-foreground">Open Tickets</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="card-stat-in-progress">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-foreground">
                  {inProgressTickets}
                </p>
                <p className="text-xs text-muted-foreground">In Progress</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="card-stat-urgent">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-rose-100 dark:bg-rose-900/30">
                <AlertCircle className="h-4 w-4 text-rose-600 dark:text-rose-400" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-foreground">
                  {urgentTickets}
                </p>
                <p className="text-xs text-muted-foreground">Urgent</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-muted-foreground" />
                Support Tickets
              </CardTitle>
              <CardDescription>
                {filteredTickets.length} ticket
                {filteredTickets.length !== 1 ? "s" : ""} found
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search tickets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 w-[200px]"
                  data-testid="input-search-tickets"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger
                  className="w-[140px]"
                  data-testid="select-status-filter"
                >
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger
                  className="w-[140px]"
                  data-testid="select-priority-filter"
                >
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priority</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : filteredTickets.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No tickets found</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="list-tickets">
              {filteredTickets.map((ticket) => (
                <div
                  key={ticket.id}
                  className="flex items-center gap-4 p-4 rounded-lg border hover-elevate cursor-pointer transition-colors"
                  onClick={() => setSelectedTicket(ticket)}
                  data-testid={`row-ticket-${ticket.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-foreground truncate">
                        {ticket.subject}
                      </p>
                      <Badge
                        className={cn(
                          "text-xs",
                          priorityColors[ticket.priority],
                        )}
                      >
                        {priorityLabels[ticket.priority]}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {ticket.userName}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDistanceToNow(new Date(ticket.createdAt), {
                          addSuffix: true,
                        })}
                      </span>
                      <Badge variant="secondary" className="text-xs">
                        {categoryLabels[ticket.category]}
                      </Badge>
                    </div>
                  </div>
                  <Badge className={cn("text-xs", statusColors[ticket.status])}>
                    {statusLabels[ticket.status]}
                  </Badge>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={!!selectedTicket}
        onOpenChange={(open) => !open && setSelectedTicket(null)}
      >
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          {selectedTicket && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <DialogTitle className="text-lg">
                      {selectedTicket.subject}
                    </DialogTitle>
                    <DialogDescription className="flex items-center gap-2 mt-1">
                      <User className="h-3 w-3" />
                      {selectedTicket.userName} ({selectedTicket.userEmail})
                    </DialogDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      className={cn(
                        "text-xs",
                        priorityColors[selectedTicket.priority],
                      )}
                    >
                      {priorityLabels[selectedTicket.priority]}
                    </Badge>
                    <Select
                      value={selectedTicket.status}
                      onValueChange={(value) =>
                        handleUpdateStatus(value as TicketStatus)
                      }
                    >
                      <SelectTrigger
                        className="w-[130px] h-8"
                        data-testid="select-ticket-status"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </DialogHeader>

              <div className="flex items-center gap-4 text-sm text-muted-foreground py-2">
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  Created {format(new Date(selectedTicket.createdAt), "PPp")}
                </span>
                <Badge variant="secondary" className="text-xs">
                  {categoryLabels[selectedTicket.category]}
                </Badge>
              </div>

              <Separator />

              <ScrollArea className="flex-1 pr-4 max-h-[300px]">
                <div className="space-y-4 py-4" data-testid="list-messages">
                  {selectedTicket.messages.map((message) => (
                    <div
                      key={message.id}
                      className={cn(
                        "p-3 rounded-lg",
                        message.senderType === "user"
                          ? "bg-muted/50"
                          : "bg-primary/10 ml-8",
                      )}
                      data-testid={`message-${message.id}`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="text-sm font-medium text-foreground">
                          {message.senderName}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(message.createdAt), {
                            addSuffix: true,
                          })}
                        </span>
                      </div>
                      <p className="text-sm text-foreground">
                        {message.message}
                      </p>
                    </div>
                  ))}
                </div>
              </ScrollArea>

              <Separator />

              <div className="space-y-3 pt-4">
                <Label htmlFor="reply">Reply to user</Label>
                <Textarea
                  id="reply"
                  placeholder="Type your response..."
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  rows={3}
                  data-testid="textarea-reply"
                />
                <div className="flex justify-end">
                  <Button
                    onClick={handleSendReply}
                    disabled={!replyMessage.trim()}
                    className="gap-2"
                    data-testid="button-send-reply"
                  >
                    <Send className="h-4 w-4" />
                    Send Reply
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
